import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Shield, CheckCircle, Clock, AlertTriangle, ChevronDown, ChevronUp, Lock, Eye, FileText, DollarSign, RefreshCw, Cpu, Database, BarChart3 } from 'lucide-react';

const DOMAIN_ICONS = { 1: Cpu, 2: BarChart3, 3: Eye, 4: Lock, 5: Database, 6: FileText, 7: DollarSign, 8: RefreshCw };
const STATUS_COLORS = { done: '#1D9E75', in_progress: '#00B4D8', partial: '#E8A020', planned: '#5C6B7A', achieved: '#1D9E75' };

export default function AIOperationsPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/ai-ops/status").then(r => setData(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div data-testid="aiops-loading"><Skeleton className="h-96 rounded-sm" /></div>;

  const summary = data?.summary || {};
  const domains = data?.domains || [];

  return (
    <div className="space-y-6" data-testid="aiops-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0D1B2A', fontFamily: "'IBM Plex Sans'" }}>AI Operations Manual</h1>
        <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>8-Domain Framework — Day 0 to Month 6 Maturity</p>
      </div>

      {/* Overall Progress */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="day0-progress">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: '#0D1B2A' }}>Day 0 Non-Negotiables</p>
              <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Mandatory baseline — required before production</p>
            </div>
            <span className="text-2xl font-bold font-mono" style={{ color: summary.overall_day0_pct >= 80 ? '#1D9E75' : '#E8A020' }}>{summary.overall_day0_pct}%</span>
          </div>
          <div className="h-3 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.12)' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${summary.overall_day0_pct}%`, background: summary.overall_day0_pct >= 80 ? '#1D9E75' : '#E8A020' }} />
          </div>
          <p className="text-[10px] mt-2" style={{ color: '#5C6B7A' }}>{summary.day0_done}/{summary.day0_total} items complete</p>
        </Card>
        <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="month6-progress">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold" style={{ color: '#0D1B2A' }}>Month 6 Targets</p>
              <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Aspirational excellence across all domains</p>
            </div>
            <span className="text-2xl font-bold font-mono" style={{ color: '#1A4C7C' }}>{summary.overall_month6_pct}%</span>
          </div>
          <div className="h-3 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.12)' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${summary.overall_month6_pct}%`, background: '#1A4C7C' }} />
          </div>
          <p className="text-[10px] mt-2" style={{ color: '#5C6B7A' }}>{summary.month6_done}/{summary.month6_total} items complete</p>
        </Card>
      </div>

      {/* Domain Cards */}
      <div className="space-y-2" data-testid="aiops-domains">
        {domains.map((domain) => {
          const isOpen = expanded === domain.key;
          const Icon = DOMAIN_ICONS[domain.domain_id] || Shield;
          const d0Color = STATUS_COLORS[domain.day0_status] || '#5C6B7A';

          return (
            <Card key={domain.key} className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white overflow-hidden">
              <button onClick={() => setExpanded(isOpen ? null : domain.key)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-[#F5F7FA] transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ background: d0Color + '15' }}>
                    <Icon className="w-4 h-4" style={{ color: d0Color }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono" style={{ color: '#5C6B7A' }}>D{domain.domain_id}</span>
                      <span className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>{domain.domain}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px] px-1.5 py-0.5 rounded-sm capitalize font-medium" style={{ background: d0Color + '15', color: d0Color }}>Day 0: {domain.day0_status}</span>
                      <span className="text-[10px]" style={{ color: '#5C6B7A' }}>
                        {domain.day0_progress.done}/{domain.day0_progress.total} baseline | {domain.month6_progress.done}/{domain.month6_progress.total} target
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-24">
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.12)' }}>
                      <div className="h-full rounded-full" style={{ width: `${domain.day0_progress.pct}%`, background: d0Color }} />
                    </div>
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4" style={{ color: '#5C6B7A' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#5C6B7A' }} />}
                </div>
              </button>

              {isOpen && (
                <div className="px-4 pb-4 border-t border-[rgba(136,150,168,0.1)] space-y-4">
                  {/* Day 0 Items */}
                  <div className="mt-3">
                    <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: '#0D1B2A' }}>Day 0 Baseline</p>
                    {domain.day0_items.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 py-1">
                        {item.status === 'done' ? <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: '#1D9E75' }} /> : <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: '#E8A020' }} />}
                        <div>
                          <p className="text-xs" style={{ color: '#0D1B2A' }}>{item.item}</p>
                          {item.evidence && <p className="text-[10px]" style={{ color: '#5C6B7A' }}>{item.evidence}</p>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Month 6 Items */}
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: '#1A4C7C' }}>Month 6 Target</p>
                    {domain.month6_items.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 py-1">
                        {item.status === 'done' ? <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: '#1D9E75' }} /> : <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: '#5C6B7A' }} />}
                        <div>
                          <p className="text-xs" style={{ color: item.status === 'done' ? '#0D1B2A' : '#5C6B7A' }}>{item.item}</p>
                          {item.evidence && <p className="text-[10px]" style={{ color: '#5C6B7A' }}>{item.evidence}</p>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Fraime Specifics */}
                  {domain.specifics && Object.keys(domain.specifics).length > 0 && (
                    <div className="p-3 rounded-sm" style={{ background: 'rgba(0,180,216,0.04)', border: '1px solid rgba(0,180,216,0.1)' }}>
                      <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: '#00B4D8' }}>Fraime Configuration</p>
                      {Object.entries(domain.specifics).map(([key, val]) => (
                        <div key={key} className="py-0.5">
                          <span className="text-[10px] font-semibold capitalize" style={{ color: '#0D1B2A' }}>{key.replace(/_/g, ' ')}: </span>
                          <span className="text-[10px]" style={{ color: '#5C6B7A' }}>
                            {Array.isArray(val) ? val.join(' | ') : typeof val === 'object' ? JSON.stringify(val) : String(val)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Monthly Cadence */}
      <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-2" style={{ color: '#0D1B2A' }}>Monthly Review Cadence</h3>
        <p className="text-xs" style={{ color: '#5C6B7A' }}>3-hour end-of-month ritual. Skip the cadence and the document ages out within 60 days.</p>
        <div className="grid grid-cols-4 gap-3 mt-3">
          {[
            { step: '1', label: 'Gap Analysis', desc: 'Review each domain against targets', time: '45 min' },
            { step: '2', label: 'Incident Review', desc: 'Any AI failures or near-misses', time: '30 min' },
            { step: '3', label: 'Cost Review', desc: 'Per-agent costs vs budgets', time: '30 min' },
            { step: '4', label: 'Priority Setting', desc: 'Next month focus areas', time: '15 min' },
          ].map(s => (
            <div key={s.step} className="p-3 rounded-sm" style={{ background: '#F5F7FA' }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ background: '#1A4C7C' }}>{s.step}</span>
                <span className="text-xs font-semibold" style={{ color: '#0D1B2A' }}>{s.label}</span>
              </div>
              <p className="text-[10px]" style={{ color: '#5C6B7A' }}>{s.desc}</p>
              <p className="text-[10px] font-mono mt-1" style={{ color: '#1A4C7C' }}>{s.time}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
